import { Icon } from '@shared/components/icon/Icon';
import { Collapsible } from '@base-ui/react/collapsible';
import cx from 'classnames';
import typography from '~styles/typography.scss';
import styles from './OccupantsAccordion.scss';
import { ServiceOccupant } from '../apiHelpers';
import { OccupantsForDate } from './OccupantsForDate';
import { useMultiSelectOccupants } from '../hooks/useMultiSelectOccupants';
import { OccupantSelectionCheckbox } from '../OccupantSelectionCheckbox';
import { useState } from 'react';

interface OccupantsAccordionProps {
  title: string;
  occupants: ServiceOccupant[];
  className?: string;
  testId?: string;
}

export const OccupantsAccordion = ({
  title,
  className,
  occupants,
  testId,
}: OccupantsAccordionProps) => {
  const currentOccupantsCount = occupants.length;
  const [initialOccupantsCount] = useState(currentOccupantsCount);
  const shouldReinstantiateWithNewDefaultState =
    [initialOccupantsCount, currentOccupantsCount].includes(0) &&
    initialOccupantsCount !== currentOccupantsCount;

  const {
    isMultiSelectingOccupants,
    updateOccupantMultiSelectionGroup,
    isOccupantMultiSelected,
  } = useMultiSelectOccupants();

  const totalCoversForGroup = occupants.reduce(
    (sum, occupant) => sum + occupant.guestCount,
    0,
  );
  const groupId = `occupant-group-${title.toLowerCase().replace(/\s+/g, '-')}`;
  const allOccupantsSelected =
    occupants.length > 0 && occupants.every((o) => isOccupantMultiSelected(o));

  const handleGroupSelectionChange = () => {
    updateOccupantMultiSelectionGroup(occupants);
  };

  return (
    <Collapsible.Root
      aria-label={`${title} occupants group`}
      className={cx(styles.collapsibleGroup, className)}
      data-testid={testId}
      disabled={currentOccupantsCount === 0}
      defaultOpen={currentOccupantsCount > 0}
      key={shouldReinstantiateWithNewDefaultState ? 'busted' : 'default'}
    >
      <Collapsible.Trigger className={styles.trigger}>
        {isMultiSelectingOccupants && (
          <span
            onClick={(e) => {
              e.stopPropagation();
            }}
            className={styles.groupMultiSelectCheckbox}
          >
            <OccupantSelectionCheckbox
              id={groupId}
              checked={allOccupantsSelected}
              onChange={handleGroupSelectionChange}
            />
          </span>
        )}
        <h2 className={cx(typography.h7, styles.title)}>{title}</h2>
        <div className={styles.countColumn}>
          <span className={typography.h7}>{currentOccupantsCount}</span>
          <span className={styles.countLabel}>Parties</span>
        </div>
        <div className={styles.countColumn}>
          <span className={typography.h7}>{totalCoversForGroup}</span>
          <span className={styles.countLabel}>Covers</span>
        </div>
        <Icon
          name="chevron"
          className={styles.chevron}
          aria-label={`Toggle ${title}`}
        />
      </Collapsible.Trigger>

      <Collapsible.Panel className={styles.panel}>
        <ul aria-label="all occupants for a date" className={styles.list}>
          <OccupantsForDate
            occupantsForDate={occupants}
            isLoadingOccupants={false}
          />
        </ul>
      </Collapsible.Panel>
    </Collapsible.Root>
  );
};
